// Anzeigeformate de-DE / Europe/Berlin nach design-system §6.4. Die eine Stelle für Geld, Mengen, Datum.
// Gerundet wird hier nie fachlich – die Funktionen stellen nur ganze Cent und Tausendstel dar.
import { divRound, scaledToDecimalString } from './decimal'
import type { Cents } from './money'
import { QTY_DECIMALS, type Qty, type UnitBreakdown } from './qty'
import { BUSINESS_TZ, berlinParts, businessDate, diffDays, isIsoDate, isoWeek, type IsoDate } from './time'

export const NBSP = ' '
export const MINUS = '−'
/** Halbgeviertstrich für leere Werte und Zeiträume */
export const EN_DASH = '–'
export const EMPTY = EN_DASH

const LOCALE = 'de-DE'

const numberFormats = new Map<string, Intl.NumberFormat>()
function numberFormat(minDigits: number, maxDigits: number): Intl.NumberFormat {
  const key = `${minDigits}:${maxDigits}`
  let format = numberFormats.get(key)
  if (!format) {
    format = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
      useGrouping: 'always',
    })
    numberFormats.set(key, format)
  }
  return format
}

/** Skalierte Ganzzahl exakt formatieren (über die Dezimal-Zeichenkette, nicht über Gleitkomma). */
function formatScaled(value: number, scale: number, minDigits: number, maxDigits: number): string {
  const text = numberFormat(minDigits, maxDigits).format(
    scaledToDecimalString(Math.abs(value), scale) as unknown as number, // Intl akzeptiert Dezimal-Zeichenketten exakt
  )
  return value < 0 ? `${MINUS}${text}` : text
}

export interface EuroOptions {
  /** false: nur die Zahl (Tabellenzellen, der Spaltenkopf trägt „€“) */
  symbol?: boolean
  /** '+' bei positiven Werten zeigen (Differenzen) */
  signed?: boolean
  /** Kennzahlen: ab 100.000 € ohne Cent */
  compact?: boolean
}

/** 123450 → „1.234,50 €“ (geschütztes Leerzeichen, echtes Minus). */
export function formatEuro(cents: Cents, options: EuroOptions = {}): string {
  const { symbol = true, signed = false, compact = false } = options
  const whole = compact && Math.abs(cents) >= 10_000_000
  let text = whole ? formatScaled(Math.round(cents / 100), 0, 0, 0) : formatScaled(cents, 2, 2, 2)
  if (signed && cents > 0) text = `+${text}`
  return symbol ? `${text}${NBSP}€` : text
}

/** Stückpreis mit vier Nachkommastellen: Betrag in Zehntausendstel Euro → „0,7410 €“. */
export function formatEuro4(tenThousandths: number): string {
  return `${formatScaled(tenThousandths, 4, 4, 4)}${NBSP}€`
}

export interface QtyOptions {
  /** Kurzform der Einheit („Ka“, „kg“) – wird mit geschütztem Leerzeichen angehängt */
  unit?: string
  /** Mindest-Nachkommastellen (Gewicht: 3 → „1,250 kg“). Standard 0 */
  minDecimals?: number
}

/** 46000 → „46“, 12500 + 'kg' → „12,5 kg“, 1248000 + 'Ka' → „1.248 Ka“. */
export function formatQty(qty: Qty, options: QtyOptions = {}): string {
  const text = formatScaled(qty, QTY_DECIMALS, options.minDecimals ?? 0, QTY_DECIMALS)
  return options.unit ? `${text}${NBSP}${options.unit}` : text
}

/** Zerlegung aus breakdownQty: „46 Ka + 12 Fl“; ein Rest wird in `baseUnit` angehängt. */
export function formatBreakdown(breakdown: UnitBreakdown, baseUnit: string): string {
  const parts = breakdown.parts.map((p) => `${formatScaled(p.count, 0, 0, 0)}${NBSP}${p.code}`)
  if (breakdown.rest !== 0) parts.push(formatQty(breakdown.rest, { unit: baseUnit }))
  if (parts.length === 0) return `0${NBSP}${baseUnit}`
  return `${breakdown.negative ? MINUS : ''}${parts.join(' + ')}`
}

/** Gebinde-Inhalt: (20, 500, 'l') → „20 × 0,5 l“. `contentQty` in Tausendstel. */
export const formatPackaging = (count: number, contentQty: Qty, contentUnit: string): string =>
  `${count}${NBSP}×${NBSP}${formatQty(contentQty, { unit: contentUnit })}`

/** Basispunkte → „31,4 %“ (eine Nachkommastelle, geschütztes Leerzeichen). */
export function formatPercent(basisPoints: number, digits = 1): string {
  // Prozentwert mit `digits` Nachkommastellen als Ganzzahl: 3140 Basispunkte, 1 Stelle → 314
  const value =
    digits >= 2
      ? basisPoints * 10 ** (digits - 2)
      : Number(divRound(BigInt(basisPoints), 10n ** BigInt(2 - digits), 'half-up'))
  return `${formatScaled(value, digits, digits, digits)}${NBSP}%`
}

/** Steuersatz: 1900 → „19 %“, 1070 → „10,7 %“. */
export function formatRate(rateBp: number): string {
  return `${formatScaled(rateBp, 2, 0, 2)}${NBSP}%`
}

export function formatNumber(value: number, maxDigits = 0): string {
  const text = numberFormat(0, maxDigits).format(Math.abs(value))
  return value < 0 ? `${MINUS}${text}` : text
}

// ---- Datum und Zeit ---------------------------------------------------------

const WEEKDAY_SHORT = ['Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.', 'So.']
const pad = (n: number): string => String(n).padStart(2, '0')

/**
 * Zeitpunkt als Date – auch aus seinem ISO-Text ('2026-10-10T12:20:00.000Z'). Der Client stellt Zeitpunkte nach dem
 * Vertrag her (web/api/dates.ts); wo trotzdem Text ankommt (Ereignisstrom, gespeicherte JSON-Felder), stürzt die
 * Anzeige nicht ab, sondern zeigt denselben Zeitpunkt.
 */
const asInstant = (value: Date | string): Date => {
  if (value instanceof Date) return value
  const date = new Date(value)
  if (typeof value !== 'string' || Number.isNaN(date.getTime())) throw new RangeError(`Kein gültiger Zeitpunkt: ${String(value)}`)
  return date
}

const toDateParts = (value: IsoDate | Date): { year: number; month: number; day: number; weekday: number } => {
  if (typeof value === 'string') {
    // Zeitpunkt als Text (mit Uhrzeit): der Berliner Tag dieses Zeitpunkts – nicht die ersten zehn Zeichen (UTC)
    if (value.length > 10 && value[10] === 'T') return berlinParts(asInstant(value))
    if (!isIsoDate(value)) throw new RangeError(`Kein gültiges Datum: ${value}`)
    const weekday = new Date(`${value}T00:00:00Z`).getUTCDay() || 7
    return {
      year: Number(value.slice(0, 4)),
      month: Number(value.slice(5, 7)),
      day: Number(value.slice(8, 10)),
      weekday,
    }
  }
  return berlinParts(value)
}

export interface DateOptions {
  /** „Sa., 11.10.2026“ */
  weekday?: boolean
  /** enge Spalten: „11.10.“ ohne Jahr, solange das Datum im Jahr von `today` liegt */
  short?: boolean
  today?: IsoDate
}

/** Kalendertag oder Zeitpunkt (dann der Berliner Tag) → „11.10.2026“. */
export function formatDate(value: IsoDate | Date, options: DateOptions = {}): string {
  const p = toDateParts(value)
  const currentYear = Number((options.today ?? businessDate()).slice(0, 4))
  const base =
    options.short && p.year === currentYear
      ? `${pad(p.day)}.${pad(p.month)}.`
      : `${pad(p.day)}.${pad(p.month)}.${p.year}`
  return options.weekday ? `${WEEKDAY_SHORT[p.weekday - 1]}, ${base}` : base
}

/** Uhrzeit in Berlin, 24 Stunden: „14:05“. Nimmt ein Date oder dessen ISO-Text. */
export function formatTime(at: Date | string): string {
  const p = berlinParts(asInstant(at))
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** Zeitpunkt: „10.10.2026 14:20“, mit `short` „10.10. 14:20“ im laufenden Jahr. Nimmt ein Date oder dessen ISO-Text. */
export function formatDateTime(at: Date | string, options: Pick<DateOptions, 'short' | 'today'> = {}): string {
  const instant = asInstant(at)
  return `${formatDate(instant, options)} ${formatTime(instant)}`
}

/** Zeitraum: „01.09.–30.09.2026“ (Halbgeviertstrich ohne Leerzeichen). */
export function formatDateRange(from: IsoDate, to: IsoDate): string {
  if (from === to) return formatDate(from)
  const a = toDateParts(from)
  const b = toDateParts(to)
  const start = a.year === b.year ? `${pad(a.day)}.${pad(a.month)}.` : formatDate(from)
  return `${start}${EN_DASH}${formatDate(to)}`
}

/** „KW 41“ */
export const formatWeek = (date: IsoDate): string => `KW${NBSP}${isoWeek(date).week}`

/** MHD mit Monatsgenauigkeit: '2027-03-01' → „03.2027“. */
export const formatMonth = (date: IsoDate): string => `${date.slice(5, 7)}.${date.slice(0, 4)}`

/**
 * Relative Angabe nur in der letzten Stunde („gerade eben“, „vor 5 Min.“), sonst absolut:
 * heute „14:20“, sonst „10.10. 14:20“ bzw. mit Jahr.
 */
export function formatRelative(value: Date | string, now: Date = new Date()): string {
  const at = asInstant(value)
  const diffMs = now.getTime() - at.getTime()
  if (diffMs >= 0 && diffMs < 60_000) return 'gerade eben'
  if (diffMs >= 0 && diffMs < 3_600_000) return `vor ${Math.floor(diffMs / 60_000)}${NBSP}Min.`
  const today = businessDate(now)
  if (businessDate(at) === today) return formatTime(at)
  return formatDateTime(at, { short: true, today })
}

/** Tagesabstand in Worten: „heute“, „morgen“, „gestern“, „in 12 Tagen“, „vor 3 Tagen“. */
export function formatDayDistance(date: IsoDate, today: IsoDate = businessDate()): string {
  const days = diffDays(today, date)
  if (days === 0) return 'heute'
  if (days === 1) return 'morgen'
  if (days === -1) return 'gestern'
  return days > 0 ? `in ${days} Tagen` : `vor ${-days} Tagen`
}

/** Restlaufzeit (MHD): „noch 12 Tage“, „noch 1 Tag“, „läuft heute ab“, „seit 3 Tagen abgelaufen“. */
export function formatRemainingDays(date: IsoDate, today: IsoDate = businessDate()): string {
  const days = diffDays(today, date)
  if (days === 0) return 'läuft heute ab'
  if (days > 0) return days === 1 ? 'noch 1 Tag' : `noch ${days} Tage`
  return days === -1 ? 'seit 1 Tag abgelaufen' : `seit ${-days} Tagen abgelaufen`
}

/** Für `<time datetime>` und Tooltips: volles Datum mit Uhrzeit und Zeitzone. */
export function formatFullTimestamp(at: Date): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone: BUSINESS_TZ, dateStyle: 'full', timeStyle: 'medium' }).format(at)
}
